/**
 * useWalletBalancePoller — Polls live wallet balance every 30s.
 * Only runs when tradeMode === 'live' and a wallet is connected.
 * Registered once in App DataLayer.
 */

import { useEffect, useRef } from 'react';
import { useTerminalStore } from '../stores/terminal.store.js';
import { getHLAccountEquity } from '../services/hyperliquidOrderService.js';

const POLL_INTERVAL_MS = 30_000;

export function useWalletBalancePoller() {
  const tradeMode     = useTerminalStore(s => s.tradeMode);
  const activeWallet  = useTerminalStore(s => s.activeWallet);
  const setLiveAccount = useTerminalStore(s => s.setLiveAccount);
  const connectWallet  = useTerminalStore(s => s.connectWallet);

  const walletRef    = useRef(activeWallet);
  const liveModeRef  = useRef(tradeMode);
  useEffect(() => { walletRef.current   = activeWallet; }, [activeWallet]);
  useEffect(() => { liveModeRef.current = tradeMode;    }, [tradeMode]);

  useEffect(() => {
    const poll = async () => {
      const wallet = walletRef.current;
      if (!wallet || liveModeRef.current !== 'live') return;

      try {
        if (wallet.type === 'metamask') {
          const { equity, available } = await getHLAccountEquity(wallet.address);
          setLiveAccount({ equity, available, positions: 0 });
          connectWallet({ ...wallet, usdBalance: equity });
        } else if (wallet.type === 'phantom') {
          // Poll SOL balance
          const res = await fetch('https://api.mainnet-beta.solana.com', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
              jsonrpc: '2.0', id: 1,
              method:  'getBalance',
              params:  [wallet.address],
            }),
          });
          const data = await res.json() as { result?: { value?: number } };
          const sol  = (data?.result?.value ?? 0) / 1e9;
          connectWallet({ ...wallet, balance: sol });
        }
      } catch { /* ignore */ }
    };

    const id = setInterval(poll, POLL_INTERVAL_MS);
    poll(); // immediate first poll
    return () => clearInterval(id);
  }, [setLiveAccount, connectWallet]);
}
